import React from 'react';
import { format, isToday, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import { hr } from 'date-fns/locale';
import { Home, Building2, Plane, Thermometer, Calendar, Plus, Palmtree, Baby, Paperclip } from 'lucide-react';
import { formatCroatianMonth } from '../../i18n/languages';
import { useYearStatus } from '../../contexts/YearStatusContext';

const statusIcons = {
  office: Building2,
  home: Home,
  vacation: Palmtree,
  sick: Thermometer,
  other: Calendar,
  business_trip: Plane,
  parental_leave: Baby
};

const statusColors = {
  office: '#3b82f6',
  home: '#10b981',
  vacation: '#079C0C',
  sick: '#ef4444',
  other: '#8b5cf6',
  business_trip: '#27BBF5',
  parental_leave: '#f59e0b'
};

// Single-column, tap-to-edit agenda for a phone screen: shows the current
// user's own days for the month, reusing the same StatusModal flow the
// desktop grid uses (via handleCellClick) rather than duplicating it.
function MobileAgenda({
  month,
  currentUser,
  getStatusForUserAndDate,
  handleCellClick,
  currentLanguage,
  t,
  isCroatianHoliday = () => false,
  getHolidayName = () => null,
  collectiveVacations = [],
  userEmploymentData = {}
}) {
  const { yearStatus } = useYearStatus();
  const currentOpenYear = yearStatus?.currentOpenYear;

  const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
  const locale = currentLanguage === 'hr' ? hr : undefined;

  const isCollectiveVacation = (date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return collectiveVacations.some(cv => {
      const cvDate = cv.date.includes('T') ? cv.date.split('T')[0] : cv.date;
      return cvDate === dateString;
    });
  };

  const isEditable = (day) => {
    if (isWeekend(day) || isCroatianHoliday(day) || isCollectiveVacation(day)) return false;

    const employmentData = userEmploymentData[currentUser];
    if (employmentData && employmentData.employment_start_date) {
      const employmentStart = new Date(employmentData.employment_start_date);
      employmentStart.setHours(0, 0, 0, 0);
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      if (dayStart < employmentStart) return false;
    }

    const today = new Date();
    if (day.getFullYear() < today.getFullYear() ||
      (day.getFullYear() === today.getFullYear() && day.getMonth() < today.getMonth())) {
      return false;
    }

    return true;
  };

  return (
    <div style={{ marginBottom: '32px' }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        {formatCroatianMonth(month, currentLanguage) ||
          format(month, 'MMMM yyyy', { locale })}
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {days.map(day => {
          const status = getStatusForUserAndDate(currentUser, day);
          const editable = isEditable(day);
          const today = isToday(day);
          const holiday = isCroatianHoliday(day);
          const holidayName = holiday ? getHolidayName(day, currentLanguage) : null;
          const collective = isCollectiveVacation(day);
          const weekend = isWeekend(day);
          const IconComponent = status ? statusIcons[status.status] : null;
          const color = status ? statusColors[status.status] : '#9ca3af';

          const isFutureYearBooking = status && status.status === 'vacation' &&
            currentOpenYear != null && day.getFullYear() > currentOpenYear;

          let label;
          if (isFutureYearBooking) {
            label = `${t(`statusTypes.${status.status}`)} (${day.getFullYear()})`;
          } else if (status) {
            label = t(`statusTypes.${status.status}`);
          } else if (collective) {
            label = currentLanguage === 'hr' ? 'Kolektivni godišnji' : 'Collective vacation';
          } else if (holiday) {
            label = holidayName;
          } else if (weekend) {
            label = currentLanguage === 'hr' ? 'Vikend' : 'Weekend';
          } else {
            label = currentLanguage === 'hr' ? 'Nije postavljeno' : 'Not set';
          }

          return (
            <div
              key={day.toISOString()}
              onClick={(e) => editable && handleCellClick(day, currentUser, currentUser, e)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderRadius: '10px',
                backgroundColor: today ? '#f0fdf4' :
                  collective ? '#fef3c7' :
                    holiday ? '#fef2f2' :
                      status && status.status === 'vacation' ? '#eafbea' : 'white',
                border: today ? '1.5px solid #22c55e' : '1px solid #e5e7eb',
                opacity: editable || status ? 1 : 0.55,
                cursor: editable ? 'pointer' : 'default'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                <div style={{ width: '44px', flexShrink: 0, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>
                    {format(day, 'EEE', { locale })}
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: today ? '#16a34a' : '#1f2937' }}>
                    {format(day, 'd')}
                  </div>
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#374151',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {label}
                  {status && status.attachment_url && (
                    <Paperclip size={13} color="#9ca3af" style={{ marginLeft: '6px', verticalAlign: 'middle' }} />
                  )}
                </div>
              </div>
              <div style={{ flexShrink: 0, marginLeft: '8px' }}>
                {IconComponent ? <IconComponent size={20} color={color} /> :
                  editable ? <Plus size={20} color="#d1d5db" /> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MobileAgenda;
